import { signal, computed } from "@preact/signals-core";
import { getLimitStatus } from "./cart-store";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ButtonState =
  | "addToCart"
  | "limitReached"
  | "soldOut"
  | "clickToBuy"
  | "extendedOffer-modal"
  | "extendedOffer-redirect"
  | "viewMore";

export interface CardConfig {
  optionSize: number;
  visibleVariantCount: number;
  hasCustomText: boolean;
  isCustomizable: boolean;
  hasImageUpload: boolean;
  extendedOfferEnabled: boolean;
  specialOfferEnabled: boolean;
  isCbsBox: boolean;
  hiddenVariantIds: Set<number>;
}

export interface BadgeResult {
  type: "exclusive-offer" | "special-offer" | "regular";
  value: number | null;
}

export interface CardSignals {
  selectedVariantId: ReturnType<typeof signal<number | null>>;
  selectedVariant: ReturnType<typeof computed<any>>;
  buttonState: ReturnType<typeof computed<ButtonState>>;
  price: ReturnType<typeof computed<number | null>>;
  compareAtPrice: ReturnType<typeof computed<number | null>>;
  badge: ReturnType<typeof computed<BadgeResult>>;
  /** Static product ID — never changes after registration. */
  productId: number;
  /** Static vendor string — never changes after registration. */
  vendor: string;
  /** Static tags array — never changes after registration. */
  tags: string[];
  /** Hidden 'ships_by' property extracted from tags. */
  shipsBy: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeBadge(
  price: number | null,
  compareAtPrice: number | null,
  specialOfferEnabled: boolean
): BadgeResult {
  if (!price || !compareAtPrice) {
    return { type: "exclusive-offer", value: null };
  }
  const raw = ((compareAtPrice - price) / compareAtPrice) * 100;
  const pct = (window as any).alwaysRoundDown ? Math.floor(raw) : Math.round(raw);
  if (pct <= 20 && specialOfferEnabled) {
    return { type: "special-offer", value: null };
  }
  return { type: "regular", value: pct };
}

function computeButtonState(
  variant: any,
  config: CardConfig,
  productType?: string
): ButtonState {
  if (!variant) return "soldOut";

  // Gift cards don't track inventory — Shopify reports qty 0 while available is true
  const isGiftCard = productType === "Gift Card";
  const qty = variant.inventoryQuantity ?? 0;
  const hasStock = isGiftCard ? Boolean(variant.available) : qty > 0;

  if (hasStock) {
    // Redirect to product page if there are genuinely multiple selectable variants,
    // or if the product requires customization. Note: we use visibleVariantCount
    // rather than optionSize — a product can have multiple option types but only
    // one actual variant (all options have a single value), in which case we can
    // add directly without navigating to the PDP.
    if (
      (config.visibleVariantCount > 1 && config.optionSize > 1) ||
      config.hasCustomText ||
      config.hasImageUpload
    ) {
      return "clickToBuy";
    }
    return "addToCart";
  }

  // Product is sold out (qty <= 0)
  // Show "Notify Me" if extended offer feature is enabled
  if (config.extendedOfferEnabled) {
    // Customizable products open the back-in-stock modal inline on the collection
    // page (even multi-option) rather than redirecting to the PDP.
    if (config.isCustomizable) {
      return "extendedOffer-modal";
    }
    if (config.optionSize > 1 || config.hasCustomText) {
      return "extendedOffer-redirect";
    }
    return "extendedOffer-modal";
  }

  if (config.isCbsBox) return "viewMore";
  return "soldOut";
}

// ─── Registry ─────────────────────────────────────────────────────────────────

interface CardEntry {
  productData: any;
  config: CardConfig;
  signals: CardSignals;
}

const cardRegistry = new Map<string, CardEntry>();

// ─── Public API ───────────────────────────────────────────────────────────────

export function registerCard(
  cardId: string,
  productData: any,
  config: CardConfig,
  initialVariantId: number | null
): CardSignals {
  if (cardRegistry.has(cardId)) {
    return cardRegistry.get(cardId)!.signals;
  }

  const productId = (productData.id ?? 0) as number;
  const vendor = ((productData.vendor as string) ?? "").trim();
  const rawTags = Array.isArray(productData.tags) ? productData.tags : [];
  const shipsByTag = rawTags.find((t: string) => t.startsWith("ships_by-"));
  const fallbackShipsBy = (window as any).defaultShippingDate as string | undefined;
  const shipsBy = shipsByTag ? shipsByTag.replace("ships_by-", "") : (fallbackShipsBy || null);
  const tags = rawTags.filter((t: string) => !t.startsWith("ships_by-"));

  // console.log(`registerCard [${cardId}]: productId=${productId}, shipsBy="${shipsBy}" (fallback: "${fallbackShipsBy}"), tags=`, rawTags);

  const selectedVariantId = signal<number | null>(initialVariantId);

  const selectedVariant = computed<any>(() => {
    const id = selectedVariantId.value;
    const variants: any[] = productData.variants ?? [];
    const found = variants.find((v) => v.id === id);
    if (found) return found;
    // Prefer first available variant as fallback
    return variants.find((v) => !config.hiddenVariantIds.has(v.id) && v.available) ?? variants[0] ?? null;
  });

  const buttonState = computed<ButtonState>(() => {
    const base = computeButtonState(
      selectedVariant.value,
      config,
      productData.type as string | undefined
    );
    if (base === "addToCart" && getLimitStatus(productId, vendor).reached) {
      return "limitReached";
    }
    return base;
  });

  const price = computed<number | null>(() => selectedVariant.value?.price ?? null);
  const compareAtPrice = computed<number | null>(
    () => selectedVariant.value?.compare_at_price ?? null
  );
  const badge = computed<BadgeResult>(() =>
    computeBadge(price.value, compareAtPrice.value, config.specialOfferEnabled)
  );

  const signals: CardSignals = {
    selectedVariantId,
    selectedVariant,
    buttonState,
    price,
    compareAtPrice,
    badge,
    productId,
    vendor,
    tags,
    shipsBy,
  };

  cardRegistry.set(cardId, { productData, config, signals });
  return signals;
}

export function changeCardVariant(cardId: string, variantId: number): void {
  const entry = cardRegistry.get(cardId);
  if (entry) {
    entry.signals.selectedVariantId.value = variantId;

    // Log variant change details for debugging
    const variant = entry.productData.variants?.find((v: any) => v.id === variantId);
    if (variant) {
      const qty = variant.inventoryQuantity ?? 0;
      const newButtonState = computeButtonState(
        variant,
        entry.config,
        entry.productData.type as string | undefined
      );
      const isExtendedOffer = newButtonState === "extendedOffer-modal" || newButtonState === "extendedOffer-redirect";

      console.group(`🔄 Variant Changed [${cardId}]`);
      console.log(`Variant ID: ${variantId}`);
      console.log(`Variant Title: ${variant.title}`);
      console.log(`📦 Availability:`);
      console.log(`  - inventoryQuantity: ${qty}`);
      console.log(`  - available flag: ${variant.available}`);
      console.log(`🎯 Extended Offer Status:`);
      console.log(`  - Is Extended Offer: ${isExtendedOffer}`);
      console.log(`  - Button State: ${newButtonState}`);
      console.log(`  - Extended Offer Enabled (config): ${entry.config.extendedOfferEnabled}`);
      console.log(`⚙️ Config:`);
      console.log(`  - optionSize: ${entry.config.optionSize}`);
      console.log(`  - hasCustomText: ${entry.config.hasCustomText}`);
      console.log(`  - hasImageUpload: ${entry.config.hasImageUpload}`);
      console.log(`  - isCbsBox: ${entry.config.isCbsBox}`);
      console.groupEnd();
    }
  }
}

export function getCardSignals(cardId: string): CardSignals | null {
  return cardRegistry.get(cardId)?.signals ?? null;
}

export function getCardData(cardId: string): any | null {
  return cardRegistry.get(cardId)?.productData ?? null;
}

export function getCardConfig(cardId: string): CardConfig | null {
  return cardRegistry.get(cardId)?.config ?? null;
}
